/**
 * @author        woody@zumper.com (Woody Anderson)
 * @copyright     Copyright (c) 2017, Zumper
 * @description   logic for Listing
 */

import { Agent, Fees, Hours, PetPolicy, SupplementalContent, Viewing } from '@zcode/bizos'
import { mediaUrl, toBuildingShortUrl, toBuildingUrl, toListingShortUrl } from '@zcode/nr-utils'
import Media, { Size } from '../bizos/media'
import { constructUrl } from '../packages/imgix'
import { Bizo, autoclass } from './base'
import { pageTitleListingDetails } from './helpers/pageTitleListingDetails'
import Listable from './listable'
import ListingBase from './listing_base'
import { MNeighborhood, scrub as scrubNeighborhood } from './neighborhood'
import { Prices } from './pricing'
import { getBedsBathsText } from './rentable/utils/getBedsBathsText'
import * as RentableUtils from './rentable_utils'

export const scrub = (data) => {
  if (!data) {
    return data
  }
  const scrubbedData = { ...data }
  if (scrubbedData.neighborhood !== undefined) {
    scrubbedData.neighborhood = scrubNeighborhood(scrubbedData.neighborhood)
  }
  return scrubbedData
}

/**
 * Listing class. (Also parent class of Floorplan)
 *
 * @type {{
  new <Data extends import('@zcode/state-type').ListingData>(
    d?: Data
  ): Readonly<import('@zcode/state-type').Listing<Data>> & Bizo<import('@zcode/state-type').Listing<Data>, Data>
  static fromJS: (
      <Data extends import('@zcode/state-type').ListingData>(
        data: Data
      ) => Readonly<import('@zcode/state-type').Listing<Data>> & Bizo<import('@zcode/state-type').Listing<Data>, Data>
    ) &
    ((data?: undefined) => undefined) &
    (<T extends 0 | 0n | false | '' | null>(data?: T) => T)
}}
 */
const Listing = autoclass(
  'Listing',
  class Listing extends ListingBase {
    constructor(data = {}) {
      super(data)
      this._data = scrub(data)
    }
  },
  {
    id: 'listing_id',
    status: 'listing_status',
    activeUnits: /** @param {import('@zcode/state-type').Listing} t */ (t) =>
      t.isActive ? t._data.unit_data?.active : 0,
    attributionLink: 'provider_url',
    address: 'address',
    agentName: 'agent_name',
    amenities: 'amenities',
    amenityTags: 'amenity_tags',
    attribution: 'attribution',
    bathrooms: 'bathrooms',
    bedrooms: 'bedrooms',
    brokerageId: 'brokerage_id',
    buildingAmenities: 'building_amenities',
    buildingAmenityTags: 'building_amenity_tags',
    buildingId: 'building_id',
    buildingName: 'building_name',
    cityState: 'city_state',
    createdOn: 'created_on',
    currencyCode: 'currency_code',
    data: 'data',
    dateAvailable: 'date_available',
    deposits: 'deposits',
    description: 'description',
    displayAddress: 'display_address',
    external: 'external',
    feedName: 'feed_name',
    fees: { name: 'fees', type: Fees, isArray: true },
    halfBathrooms: 'half_bathrooms',
    hasNormalListingData: (/** @type {import('@zcode/state-type').Listing} */ t) =>
      'min_price' in t._data,
    hours: { name: 'hours', type: Hours, isArray: true },
    imageIds: 'image_ids',
    isFeatured: 'is_featured',
    isMessageable: 'is_messageable',
    leaseTerms: 'lease_terms',
    leaseType: 'lease_type',
    leasingFee: 'leasing_fee',
    listedOn: 'listed_on',
    listingAgents: 'listing_agents',
    listingId: 'listing_id',
    listingLocation: 'listing_location',
    listingStatus: 'listing_status',
    // listingType: 'listing_type',
    maxLeaseDays: 'max_lease_days',
    maxPrice: 'max_price',
    mlsListingId: 'mls_listing_id',
    previousPrice: 'previous_price',
    media: { name: 'media', type: Media, isArray: true },
    minLeaseDays: 'min_lease_days',
    minPrice: 'min_price',
    modifiedOn: 'modified_on',
    neighborhoodUrl: 'neighborhood_url',
    parsedData: 'parsed_data',
    paShouldIndex: 'pa_should_index',
    paUrl: 'pa_url',
    pbId: 'pb_id',
    pbUrl: 'pb_url',
    petPolicy: { name: 'pet_policy', type: PetPolicy, isArray: true },
    pets: 'pets',
    phone: 'phone',
    plId: 'pl_id',
    plUrl: 'pl_url',
    price: 'price',
    prices: { name: 'prices', type: Prices, isArray: true },
    priceChange: 'price_change',
    promotion: 'promotion',
    propertyType: 'property_type',
    providerUid: 'provider_id',
    providerUrl: 'provider_url',
    rivals: 'rivals',
    squareFeet: 'square_feet',
    supplementalContent: { name: 'supplemental_content', type: SupplementalContent },
    title: 'title',
    type: 'listing_type',
    zappable: 'zappable',
    agent: { name: ['listing_agents', 0], type: Agent },
    similar: { name: 'similar', type: Listable, isArray: true },
    unitData: 'unit_data',
    viewings: { name: 'viewings', type: Viewing, isArray: true },
    neighborhood: { name: 'neighborhood', type: MNeighborhood },
    /**
     *
     * @param {import('@zcode/state-type').Listing} t
     */
    minSquareFeet: (t) => {
      return t.unitData?.min_square_feet ?? t.squareFeet
    },
    /**
     *
     * @param {import('@zcode/state-type').Listing} t
     */
    maxSquareFeet: (t) => {
      return t.unitData?.max_square_feet ?? t.squareFeet
    },
    isFull: () => true,
    /**
     *
     * @param {import('@zcode/state-type').Listing} t
     */
    bedText: (t) => {
      if (t.bedrooms < 0) {
        return ''
      }
      return t.bedrooms === 0 ? 'studio' : `${t.bedrooms} bedroom`
    },
    /**
     *
     * @param {import('@zcode/state-type').Listing} t
     */
    buildingUrl: (t) => {
      return t.buildingId
        ? toBuildingUrl(
            t.buildingId,
            t.buildingName,
            t.address,
            t.city,
            t.state,
            t.neighborhoodName,
            t.zipcode
          )
        : undefined
    },
    /**
     *
     * @param {import('@zcode/state-type').Listing} t
     */
    dateAvailableText: (t) => (/** @type {number} */ now) =>
      RentableUtils.getValidDateAvailable(t.dateAvailableDate, now),
    /**
     *
     * @param {import('@zcode/state-type').Listing} t
     */
    floorplanTitle: (t) => t.title || getBedsBathsText(t),
    fullHalfBathroomTxt: RentableUtils.getFullHalfBathroomTxt,
    /**
     *
     * @param {import('@zcode/state-type').Listing} t
     */
    shortUrl: (t) =>
      t.buildingId ? toBuildingShortUrl(t.buildingId) : toListingShortUrl(t.listingId),
    /**
     * the one case where bubbleId impl != uId b/c bubbleId will upgrade to pb/building if this
     * listing belongs to one, whereas uId will continue to identify the floorplan uniquely
     *
     * @param {import('@zcode/state-type').Listing} t
     */
    bubbleId: (t) => {
      if (t.pbId) {
        return `b-p${t.pbId}`
      }
      if (t.buildingId) {
        return `b-${t.buildingId}`
      }
      if (t.plId) {
        return `l-${t.plId}p`
      }
      if (t.listingId) {
        return `l-${t.listingId}`
      }
    },
    pageTitle: pageTitleListingDetails,
    /**
     *
     * @param {import('@zcode/state-type').Listing} t
     */
    providerId: (t) => RentableUtils.parseProviderIdentifier(t._data.provider_id),
    providerIdOrigin: 'provider_id',
    /**
     *
     * @param {import('@zcode/state-type').Listing} t
     */
    providerSource: (t) => RentableUtils.parseProviderSource(t._data.provider_id),
    /**
     *
     * @param {import('@zcode/state-type').Listing} t
     */
    uId: (t) => (t.plId ? `l-${t.plId}p` : `l-${t.listingId}`),
    /**
     *
     * @param {import('@zcode/state-type').Listing} t
     */
    isZappable: (t) => RentableUtils.isZappable(t),
    /**
     *
     * @param {import('@zcode/state-type').Listing} t
     */
    transparentCompanyLogo: (t) => {
      const imageId = t?.parsedData?.custom_branding?.transparent_company_logo
      if (imageId) {
        return {
          id: imageId,
          url: constructUrl(mediaUrl(imageId, Size.XLARGE), { h: 30, fit: 'clip' }),
        }
      }
      return null
    },
  }
)
export default Listing
